/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-organisation-permissions.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tabs } from '@mui/material';
import Tab from '@mui/material/Tab';
import { pick } from 'lodash';
import WarningLostDataDialog from '@components-clean/legacy/unsaved-changes-dialog/warning-lost-data-dialog';
import { useAuthorization, useTranslation, useBoolean } from 'hooks';
import { IPermission } from 'interfaces/permission.interface';
import {
    OrganisationRoleFields,
    UserAndRoleFields_User_role_OrganisationRole_rights_OrganisationRoleRight as OrganisationRoleRightFields,
} from 'queries/generated/fragments.generated';
import { GET_ORGANISATION_MEMBERS_AND_ROLES, SAVE_ORGANISATION_ROLE } from 'queries/uam';
import Constants from 'utils/constants';
import NotificationService from 'utils/notification';
import DataPermissionsGrid from 'views/components/admin/permissions/data-permissions-grid';
import ScreenPermissionsGrid from 'views/components/admin/permissions/screen-permissions-grid';
import { SaveButton } from 'views/components/core/buttons';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import SelectWithCategories from 'views/components/core/select-with-categories';
import Error from 'views/components/error/error';

import './admin-organisation-permissions.scss';

const { possiblePermissions } = Constants;

const rightFields = ['moduleId', 'permissions', 'element'];

const AdminOrganisationPermissions = () => {
    const { t } = useTranslation();
    const { roleId } = useParams();
    const user = useAuthorization();
    const navigate = useNavigate();
    const [saveOrganisationRole] = useMutation(SAVE_ORGANISATION_ROLE);
    const [currentRole, setCurrentRole] = useState(Constants.ObjectId.Empty);
    const [bufferRole, setBufferRole] = useState(null);
    const [bufferTab, setBufferTab] = useState(null);
    const [roleInfo, setRoleInfo] = useState(null);
    const [active, setActive] = useState(0);
    const [updatedPermissions, setUpdatedPermissions] = useState([]);
    const isEditable = useBoolean(false);
    const saving = useBoolean(false);
    const showWarningModal = useBoolean(false);

    const { organisationId } = user;
    const canEdit = user?.canWriteSystemAdminLevel(true);

    // Use GET_ORGANISATION_MEMBERS_AND_ROLES here to get the data
    const [loadRoles, { data, error, loading }] = useLazyQuery(GET_ORGANISATION_MEMBERS_AND_ROLES);

    const changeRole =
        () =>
        (event: any, checkForChanges = true) => {
            const isChangesApplied =
                updatedPermissions?.filter((item: OrganisationRoleRightFields) => item.permissions).length !==
                roleInfo?.rights?.filter((item: OrganisationRoleRightFields) => item.permissions).length;
            if (!isChangesApplied && checkForChanges && isEditable.value) {
                showWarningModal.setTrue();
                setBufferRole(event.target.value);
            } else {
                isEditable.setFalse();
                setCurrentRole(event.target.value);
                const role = data?.organisation.roles.find(
                    (item: OrganisationRoleFields) => item.id === event.target.value,
                );
                setRoleInfo(role);
                setUpdatedPermissions(role?.rights);
            }
        };

    useEffect(() => {
        loadRoles();
    }, [organisationId]);

    useEffect(() => {
        if (roleId && data?.organisation) {
            const value = {
                target: {
                    value: roleId,
                },
            };
            changeRole()(value);
        } else if (data?.organisation) {
            const role = data?.organisation.roles.find((item: OrganisationRoleFields) => item.id === roleInfo?.id);
            setRoleInfo(role);
            setUpdatedPermissions(role?.rights);
        }
    }, [roleId, data]);

    if (!user || !organisationId || !user.isOrgAdmin()) return null;

    if (loading) return <Loading />;

    if (error) return <Error error={error} />;

    const savePermissions = () => {
        const value = updatedPermissions.map((item) => pick(item, rightFields));
        saving.setTrue();
        saveOrganisationRole({
            variables: {
                organisationRole: {
                    id: roleInfo.id,
                    name: roleInfo.name,
                    description: roleInfo.description,
                    modules: roleInfo.modules,
                    rights: value,
                    active: roleInfo.active,
                },
            },
            refetchQueries: [
                {
                    query: GET_ORGANISATION_MEMBERS_AND_ROLES,
                },
            ],
        })
            .then(() => {
                NotificationService.showNotification(
                    active === 0
                        ? t('uam:screenPermissionsSuccessfulUpdate')
                        : t('uam:dataPermissionsSuccessfulUpdate'),
                );
                isEditable.setFalse();
                saving.setFalse();
            })
            .catch((e: any) => {
                console.log('Update permissions error: ', e);
                NotificationService.showNotification(t('errorMessage'));
                isEditable.setFalse();
                saving.setFalse();
            });
    };

    const roles =
        data?.organisation.roles.map((item: OrganisationRoleFields) => ({
            id: item.id,
            text: item.name,
        })) ?? [];

    roles.unshift({
        id: Constants.ObjectId.Empty,
        text: t('selectRole'),
    });

    const screenPermissions = possiblePermissions
        .filter((item: any) => item.element.match(/^page/))
        .map((item: any) => ({
            ...item,
            moduleName: Constants.moduleName[item.moduleId],
            permissions: item.permissions || item.possiblePermissions,
        }));

    const dataPermissions = possiblePermissions
        .filter((item: any) => item.element.match(/^data/))
        .filter((item: any) => !item.hidden)
        .map((item: any) => ({
            ...item,
            permissions: item.permissions || item.possiblePermissions,
        }));

    return (
        <>
            <WarningLostDataDialog
                open={showWarningModal.value}
                onClose={(action: string) => {
                    if (action === 'close') {
                        if (bufferRole) {
                            changeRole()(
                                {
                                    target: {
                                        value: bufferRole,
                                    },
                                },
                                false,
                            );
                            setBufferRole(null);
                        }
                        if (bufferTab !== null) {
                            isEditable.setFalse();
                            setActive(bufferTab);
                            setBufferTab(null);
                        }
                    }
                    showWarningModal.setFalse();
                }}
            />
            <PageTitle
                text={t('uam:manageOrganisationPermissions')}
                backButton={
                    roleId ? (
                        <span
                            className="back-btn-permissions"
                            id="back-btn-permissions"
                            onClick={() => {
                                navigate(-1);
                            }}
                        >
                            <FontAwesomeIcon icon={['fas', 'arrow-circle-left']} className="back-btn-icon" />
                            <p className="back-btn-title">{t('uam:manageRoles')}</p>
                        </span>
                    ) : null
                }
            />
            <br />
            <SelectWithCategories
                showWarning={false}
                customClassName="type-edc-wrapper"
                fieldSelect="role"
                label={t('role')}
                filterable={false}
                searchable
                currentState={currentRole}
                onChange={changeRole}
                customId="manage-permissions-roles"
                dataArray={roles}
            />
            <Tabs
                className="permissions-tabs"
                value={active}
                onChange={(e, newValue) => {
                    const isChangesApplied =
                        updatedPermissions?.filter((item: any) => item.permissions).length !==
                        roleInfo?.rights?.filter((item: any) => item.permissions).length;
                    if (isChangesApplied && isEditable.value) {
                        showWarningModal.setTrue();
                        setBufferTab(newValue);
                    } else {
                        isEditable.setFalse();
                        setActive(newValue);
                    }
                }}
            >
                <Tab
                    className="permissions-tab"
                    label={t('uam:screenPermissions')}
                    data-testid="screen-permissions-tab"
                />
                <Tab className="permissions-tab" label={t('uam:dataPermissions')} data-testid="data-permissions-tab" />
            </Tabs>
            {roleInfo ? (
                <>
                    <div className="saving-btn-wrapper">
                        {saving.value && <p className="saving-status">{t('saving')}</p>}
                        <SaveButton
                            className="permissions-btn"
                            id={active === 0 ? 'update-screen-permissions' : 'update-data-permissions'}
                            text={isEditable.value ? 'common:done' : 'common:edit'}
                            disabled={saving.value || !canEdit}
                            onClick={() => {
                                if (isEditable.value) {
                                    savePermissions();
                                } else {
                                    isEditable.setTrue();
                                }
                            }}
                        />
                    </div>
                    <br />
                </>
            ) : null}
            {active === 0 && (
                <ScreenPermissionsGrid
                    role={roleInfo}
                    screenPermissions={screenPermissions}
                    isEditable={isEditable.value}
                    updatePermissions={(permissions: IPermission[]) => {
                        let array: any = [...permissions];
                        // The save replaces the role's full rights list, so also carry over the role's existing
                        // rights for feature-flag-hidden elements (e.g. RiskIQ) - otherwise saving would drop them.
                        array.push(
                            ...updatedPermissions.filter(
                                (item: any) =>
                                    item.element &&
                                    (item.element.match(/^data/) || Constants.isFeatureHiddenPermission(item.element)),
                            ),
                        );
                        array = array.map((item: IPermission) => ({
                            moduleId: item.moduleId,
                            element: item.element,
                            permissions: item.permissions ?? item.possiblePermissions,
                        }));
                        setUpdatedPermissions(array);
                    }}
                />
            )}
            {active === 1 && (
                <DataPermissionsGrid
                    role={roleInfo}
                    possiblePermissions={dataPermissions}
                    isEditable={isEditable.value}
                    updatePermissions={(permissions: IPermission[]) => {
                        let array: any = [...permissions];
                        // The save replaces the role's full rights list, so also carry over the role's existing
                        // rights for feature-flag-hidden elements (e.g. RiskIQ) - otherwise saving would drop them.
                        const pagePermissions = [...updatedPermissions].filter(
                            (item: any) =>
                                item.element &&
                                (item.element.match(/^page/) || Constants.isFeatureHiddenPermission(item.element)),
                        );
                        array.push(...pagePermissions);
                        array = array.map((item: IPermission) => ({
                            moduleId: item.moduleId,
                            element: item.element,
                            permissions: item.permissions,
                        }));
                        setUpdatedPermissions(array);
                    }}
                />
            )}
        </>
    );
};

export default AdminOrganisationPermissions;
